import React from 'react';
import { SupportedLeadCardType } from '~/cards/types/supportedLeadCardTypes';
import type { OptionalSEOData } from '../types';
import { ListInColumns, Star } from './optionalContent.styles';

// As much as possible, this file should contain ALL optional SEO copy for ALL card types.
// This is because the unfiltered PLPs are typically more important than the filtered ones from an SEO perspective.
// TODO: Add missing content for DIGITAL_SAVE_THE_DATE, PLACE, WEDDING_SHOWER_INVITATION https://newamsterdamlabs.atlassian.net/browse/PE-397

export const CARD_TYPE_COPY: Record<SupportedLeadCardType, OptionalSEOData | undefined> = {
  MATCHING_SUITES: {
    subheading: 'Solidify your wedding style with matching invitation suites.',
    summary: (
      <>
        <p>
          Matching suites, also called wedding invitation sets, allow you to customize what you send
          to guests and what they’ll see on your wedding day itself. Because everything coordinates,
          wedding invitation sets are a chance to bring your wedding theme to life and set the vibe
          for your celebration. Simply choose a design that speaks to you and use it throughout.
          Couples often choose to include some combination of the following:
        </p>
        <ListInColumns>
          <a href="/wedding-planning/save-the-date/shop">Save the dates</a>
          <a href="/wedding-planning/invitations/shop">Invitations</a>
          <a href="/wedding-planning/enclosures/shop">Enclosure cards</a>
          <a href="/wedding-planning/programs/shop">Programs</a>
          <a href="/wedding-planning/place-cards/shop">Place cards</a>
          <a href="/wedding-planning/menus/shop">Menus</a>
        </ListInColumns>
        <p>
          Streamline your wedding planning with paper that coordinates and set the tone for your big
          day. Customize everything from fonts to foils, and even match your design to your Zola{' '}
          <a href="/wedding-planning/website">wedding website</a>.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What is the difference between a wedding invitation and a wedding suite?',
        body: (
          <>
            <p>
              Wedding suites are coordinating collections of paper. A wedding suite includes a
              wedding invitation, but also features much more. At Zola, you&apos;ll discover
              everything you need while wedding planning, like invitations and RSVP cards. But
              you&apos;ll also everything you need for the big day itself, like programs for the
              ceremony, and useful reception cards and paper, like place cards and menus.
            </p>
          </>
        ),
      },
      {
        subheading: 'What are the essential stationery items in a wedding suite?',
        body: (
          <>
            <p>
              To create a cohesive feel, start with save the dates, invitations, and programs.
              Depending on the style and setup of your wedding, you might also add enclosure cards,
              RSVP cards, menus, and place cards. Wedding gifts rolling in quickly? Consider
              matching thank you cards, too.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we customize our wedding invitation suite?',
        body: (
          <>
            <p>
              The first step is to choose a design that reflects your wedding theme. Greenery,
              watercolor, and boho are all trending, but go with what feels true to you. Some
              couples prefer a clean, simple design with a monogram of your initials. From there,
              customize your font and colors. You can also add extras like envelope liners and
              letterpress printing.
            </p>
            <p>
              There are other ways for your personal style to shine through, too. Consider adding
              festive DIY envelope inserts to items like your save the date cards and invitations.
              You might include dried flowers, glitter, or confetti. In lieu of traditional vellum
              belly bands and wax steals, you can also attach your response cards to your
              invitations with materials like twine or ribbon. Play around!
            </p>
          </>
        ),
      },
      {
        subheading: 'What is the best way to organize a wedding suite?',
        body: (
          <>
            <p>
              It all depends on the style of your wedding day and tone you want to set. If you have
              a lot of information to convey, or want to go all out with your wedding theme,
              consider a full wedding suite. It&apos;s up to you!
            </p>
          </>
        ),
      },
      {
        subheading: 'How much does it cost to mail a wedding suite?',
        body: (
          <>
            <p>
              Cost will depend on how many guests you&apos;re inviting, and whether you have to mail
              anything internationally. Take advantage of Zola&apos;s free envelopes and guest
              addressing to save. Plus, your order ships free!
            </p>
          </>
        ),
      },
      {
        subheading: 'What free perks do we get with our order?',
        body: (
          <>
            <p>
              You&apos;ll get free envelopes, and change the dates (just in case). Don&apos;t forget
              to create your free matching wedding website, too.
            </p>
            <p>
              Our free guest addressing is also a massive timesaver you shouldn&apos;t overlook.
              While hiring an artist who specializes in calligraphy is a great option, it can take a
              bit longer. We&apos;ll quickly print your guest&apos;s name and address, as well as
              your own return address, at no extra cost.
            </p>
            <p>
              You can also expect great service and support with your Zola order. Get assistance
              with everything from order tracking to{' '}
              <a
                href="/expert-advice/how-to-word-wedding-invitations"
                target="_blank"
                rel="noopener noreferrer"
              >
                invitation wording
              </a>
              . Whatever you need, we&apos;re here to help.
            </p>
          </>
        ),
      },
    ],
  },
  INVITATION: {
    subheading: 'Invitations so beautiful, it hurts to send them away.',
    summary: (
      <>
        <p>
          Your wedding invitations should be an expression of your personal wedding style or theme.
          Many couples choose to make their wedding invitation design an artistic focal point of
          their big day, as the invitation is one of the pillars of your wedding journey.
        </p>
        <p>
          Creating DIY wedding invites as unique as you two is fun and easy at Zola. You can choose
          from eight different paper options (like recycled, pearlescent, and linen), countless font
          styles, luxurious foil accents, and tons of beautiful backgrounds and styles. We are
          always updating our site and adding new beautiful, affordable wedding invitation templates
          to help you find your perfect design and make your decision process a breeze.
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'When should we send out our wedding invitations & RSVP cards?',
        body: (
          <>
            <p>
              The traditional time period to send out your wedding invites is 6-8 weeks before the
              ceremony. Ideally, you would have already sent your{' '}
              <a
                href="/wedding-planning/save-the-date/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                save the dates
              </a>{' '}
              4–6 months in advance, which allows guests the perfect amount of time to send back an
              RSVP card, while still giving you an accurate count of who is likely to attend on your
              wedding day.
            </p>
            <p>
              Running behind on your wedding planning? No problem! Most of Zola’s wedding invites
              can be sent to you within 5-10 business days, so you’ll still have plenty of time to
              mail your custom & personalized wedding invitations to guests after you receive your
              order.
            </p>
          </>
        ),
      },
      {
        subheading: 'What should we include with our invitations?',
        body: (
          <>
            <p>
              There is more to a wedding invitation than just the invitation itself. You can think
              of your wedding invite as more of a wedding starter kit. Contained inside the wedding
              envelope is all the info your guests will need to celebrate with you on your wedding
              day. Here’s a list of what couples often include in their wedding envelopes:
            </p>
            <ul>
              <li>The invitation</li>
              <li>A self-addressed, postmarked RSVP card</li>
              <li>Enclosure card(s)</li>
            </ul>
            <p>
              At Zola, your wedding invitations come with free matching wedding envelopes. As
              always, enjoy free free guest addressing, and, if your date changes, free change the
              dates with all save the date and invitation purchases.
            </p>
          </>
        ),
      },
      {
        subheading: 'What info should we include on our wedding stationery?',
        body: (
          <>
            <p>
              Your wedding invitation is your guest&apos;s first look into your special day. You
              don&apos;t want your invite to be overwhelming, so stick to the basic information and
              keep your wedding invitation wording short and sweet. Here are some details couples
              often include when customizing our templates:
            </p>
            <ul>
              <li>Names of the parents</li>
              <li>Name of the couple</li>
              <li>Date and time of the ceremony</li>
              <li>Location of the ceremony</li>
              <li>Location of the reception (if different than ceremony site)</li>
            </ul>
            <p>
              Remember, you can always include an{' '}
              <a href="/wedding-planning/enclosures/shop" target="_blank" rel="noopener noreferrer">
                enclosure card
              </a>{' '}
              with additional important info. You can also share your{' '}
              <a href="/wedding-planning/website" target="_blank" rel="noopener noreferrer">
                wedding website
              </a>{' '}
              URL on your wedding invitations to direct guests to find even more details they need,
              like your schedule, hotel block info, FAQs, and more. At Zola, you can match your
              invitations to a free, beautiful wedding website where guests can get all your wedding
              info, shop your Zola registry, and RSVP online—all in one convenient place.
            </p>
          </>
        ),
      },
      {
        subheading: "What does Zola's wedding invitation paper feel like?",
        body: (
          <p>
            From recycled to pearlescent, linen, double thick, and more, each of our paper types
            offers a different gloss and thickness—there’s truly something for every couple, style,
            and wedding theme. Not sure which paper type to go with? Order{' '}
            <a
              href="/wedding-planning/invitations/shop?multi_sample_mode=multisampleenabled"
              target="_blank"
              rel="noopener noreferrer"
            >
              a free sample
            </a>{' '}
            to touch and feel our paper in person. Or customize one of our wedding invitation
            templates and get a free personalized sample to see your wedding details printed IRL
            before you order. You can order free sample wedding invitations anytime. Love your
            design? Place your full order in one click!
          </p>
        ),
      },
      {
        subheading: 'How do we design our wedding invitations?',
        body: (
          <>
            <p>
              A common way for couples to design their invitation and wedding stationery is to start
              with a template and customize it to make it unique. Our fabulous DIY wedding
              invitation templates are all super easy to personalize with your wedding details. This
              is an especially great option for people who have a hard time choosing a wedding
              invitation design.
            </p>
            <p>
              Still not sure where to start? Match your invite to your wedding theme. Set the tone
              for your big day with a romantic calligraphy font or whimsical watercolor design. You
              can also create your own design and upload it to Zola to take advantage of our free
              guest addressing and free envelopes. Whether you’re choosing a custom wedding
              invitation or popular design, remember: this is your special day, and Zola is here to
              help you get the wedding invites you want.
            </p>
          </>
        ),
      },
    ],
    reviews: (
      <>
        <p>
          <strong>Amanda & Rachel</strong>
          <br />
          <span>
            <Star />
            <Star />
            <Star />
            <Star />
            <Star />
          </span>
          <br />
          <strong>Free samples!</strong>
          <br />
          We got five free samples of our favorite invitation designs to test out before we made a
          decision. Shipping was free, too!
        </p>
        <p>
          <strong>Steven & Alex</strong>
          <br />
          <span>
            <Star />
            <Star />
            <Star />
            <Star />
            <Star />
          </span>
          <br />
          <strong>The most helpful site ever</strong>
          <br />
          We had no clue what we had to put on our invitations, and Zola answered even our craziest
          questions.
        </p>
      </>
    ),
  },
  SAVE_THE_DATE: {
    subheading: 'Designer save the dates on a DIY budget.',
    summary: (
      <>
        <p>
          Save the date cards should be simple and fun. This is just a quick way to let guests know
          a wedding is on the horizon and to keep their calendar clear. Personalization is a great
          way to express yourself without having to say a lot.
        </p>
        <p>
          Start off by choosing a save the date template design that matches your wedding theme.
          Whether it’s a vintage postcard with rustic details, a greenery save the date featuring
          watercolor accents, or a formal save the date design echoing your elegant wedding day,
          find a save the date for every budget and style.
        </p>
        <p>
          At Zola, you can customize your wedding save the dates with a favorite photo, font, or
          template. Personalize your invitation wording, choose from beautiful paper types (like
          recycled, pearlescent, and linen), add custom foil, and even make it a magnet! Save the
          date magnets are a popular way to announce your upcoming wedding. No one will forget your
          wedding date if it’s posted on their fridge!
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'What should we write on our save the date cards?',
        body: (
          <p>
            We recommend keeping it simple. Traditionally, a save the date card, postcard, or magnet
            includes the names of the couple, the city and state where the ceremony will take place,
            and, of course, the date! While those are just the essentials, many couples also include
            an engagement photo, striking design, or a fun, personal message like “We can’t wait to
            celebrate together!”, or “See you soon!”. Zola can help you create custom save the date
            cards and match them to your free Zola wedding website. Enjoy free free guest
            addressing, free envelopes, and, if your date changes, free change the dates with all
            save the date and invitation purchases. Whether you go with our save the date cards or
            our save the date magnets, we make it as easy as possible to create and send your save
            your dates.
          </p>
        ),
      },
      {
        subheading: 'Do we need a save the date wedding invitation?',
        body: (
          <p>
            While it’s totally up to you, we recommend sending a save the date card, so guests can
            save your wedding date, plan accordingly, and anticipate your formal invitation. Your
            save the date cards are also a great way to set the tone for your big day, from your
            wedding theme to formality and more. Zola has free personalized samples and free
            addressing, so you can always get a save the date card with your wedding details printed
            IRL before you order the rest of your wedding stationery. Love your design? You can
            place your full order in one click—easy!
          </p>
        ),
      },
      {
        subheading: 'When should we send wedding save the dates?',
        body: (
          <>
            <p>
              You can send out your save the date as soon as you have a date locked in! Typically,
              couples will opt to send their save the date invitations between eight and six months
              before the day of the wedding. Here are a few do&apos;s and don&apos;ts for when to
              send save the date cards to your friends and family.
            </p>
            <b>Do:</b>
            <ul>
              <li>Send them before the 6-month mark</li>
              <li>
                Provide extra time if it’s a destination wedding (up to 12 months in advance for
                weddings out of the country, so that guests can make travel arrangements)
              </li>
            </ul>
            <b>Don&apos;t:</b>
            <ul>
              <li>Send without having a venue and location locked down for your date</li>
              <li>
                Send a save the date postcard longer than 12 months in advance—people might forget
              </li>
            </ul>
          </>
        ),
      },
      {
        subheading: 'Who gets a save the date invitation?',
        body: (
          <p>
            Typically, save the date cards are reserved for people who are definitely going to
            receive an invite. Whether you are sending save the date magnets or save the date
            postcards, you need to be thoughtful about who you send them to. The last thing you
            would want is to send a save the date invitation to someone who you’re not planning to,
            or you’re unable to, invite to your wedding. At Zola, you can use our free guest list
            manager to build your guest list in no time, help you stay organized throughout the
            wedding planning process, and send your complete wedding invitation suite. We’ll help
            you collect wedding guests’ addresses and print them on your envelopes for free! You can
            also use our free tool to track wedding guests’ RSVPs, meal preferences, plus ones, and
            more.
          </p>
        ),
      },
    ],
    reviews: (
      <>
        <p>
          <strong>Laura & Kwame</strong>
          <br />
          <span>
            <Star />
            <Star />
            <Star />
            <Star />
            <Star />
          </span>
          <br />
          <strong>Zola is such a timesaver</strong>
          <br />
          We loved that our save the date had matching invitations AND a wedding website. We made
          one decision and crossed three things off our to-do list, which is always awesome.
        </p>
        <p>
          <strong>Charlotte & Marco</strong>
          <br />
          <span>
            <Star />
            <Star />
            <Star />
            <Star />
            <Star />
          </span>
          <br />
          <strong>Peace of mind</strong>
          <br />
          Because of the uncertainty of the last year, we were worried that our date might change.
          Including free change the dates with our order made us feel so much better.
        </p>
        <p>
          <strong>Derek & Iain</strong>
          <br />
          <span>
            <Star />
            <Star />
            <Star />
            <Star />
            <Star />
          </span>
          <br />
          <strong>Everything works together</strong>
          <br />
          Zola makes it easy to collect everyone’s addresses, and then fills out all your envelopes
          for you. Seriously, we were dreading it so much, but we barely had to do a thing.
        </p>
      </>
    ),
  },
  DIGITAL_SAVE_THE_DATE: {
    subheading: (
      <>
        Save time, money, <em>and</em> the planet.
      </>
    ),
    summary: '',
    faqContent: [],
  },
  CHANGE_THE_DATE: {
    summary: (
      <p>
        If your wedding plans are shifting, change the date cards are a great way to prepare guests
        for a new wedding date or to deliver a postponement announcement. While you can call, email,
        or text guests with the update, a postponement card is an easy and stylish way to get the
        word out.
      </p>
    ),
    faqContent: [
      {
        subheading: 'When should we send a change the date card?',
        body: (
          <p>
            Once you know your original wedding date is no longer an option and you’ve made the
            decision to postpone, you should send a change the date card as soon as you can. Your
            guests will want to know the new dates and times so they can put your wedding on their
            calendars and start planning. Whether you are pivoting due to a personal issue, adapting
            to a booking problem, or reacting to a local or global crisis, changing the date is
            sometimes necessary. Unforeseen circumstances happen, and, when they do, Zola makes it
            easy for you to gracefully pivot away from your original date with fully customizable
            change the date cards. Plus, to help you out with your wedding planning, we’ve made
            change the dates free with all save the date and wedding invitation purchases.
          </p>
        ),
      },
      {
        subheading: 'Why is a change the date announcement important?',
        body: (
          <p>
            A change the date notice is important for the same reason a{' '}
            <a
              href="/wedding-planning/save-the-date/shop"
              target="_blank"
              rel="noopener noreferrer"
            >
              save the date
            </a>{' '}
            is important. You want the important people in your lives to be able to attend your
            wedding. Telling your guests the new dates and event times will allow them to reserve
            this new day on their calendars and adjust their schedules and plan travel,
            accommodations, time off, and more for your big day. It is especially important to use
            change the dates if you&apos;re hosting a destination wedding, where most guests have to
            make flight and hotel reservations. Change the dates let your guests know you care
            enough about their schedule to keep them, and their calendars, in the loop.
          </p>
        ),
      },
      {
        subheading: 'Can’t we just update the date on the wedding invitation?',
        body: (
          <>
            <p>
              Simply updating the date on the{' '}
              <a
                href="/wedding-planning/invitations/shop"
                target="_blank"
                rel="noopener noreferrer"
              >
                wedding invitation
              </a>{' '}
              or stationery might go unnoticed by your guests. Zola recommends sending out separate
              change the date cards for several reasons:
            </p>
            <ol>
              <li>
                It makes things very clear for your wedding guests. If you send out a save the date
                and then simply change the date on the invite, you may end up fielding a lot of
                unnecessary phone calls from guests asking to confirm which date they should plan
                for. Plus, your guests might not even notice the new date on the invitation. You
                wouldn’t want anyone showing up to your venue on your original date only to find out
                that the wedding is not happening, right? To avoid confusion, we recommend sending a
                change the date card.
              </li>
              <li>
                It’s a polite way to say that even though you had to transition away from the
                original plan, you respect your guests’ schedules and still want them to attend your
                special day. A change of plans may be tough for your guests to deal with, especially
                if they’ve already reserved flights, hotels, and time off from work. A change the
                date card allows you to gracefully pivot to a new day.
              </li>
            </ol>
          </>
        ),
      },
      {
        subheading: 'Do we have to specify a new date?',
        body: (
          <>
            <p>
              You don’t have to, but if you know the new date, definitely share it. With change the
              dates you should try your best to have a new date in place before you send out your
              change the date cards.
            </p>
            <p>
              The caveat to this is if your wedding is rapidly approaching (within two months) and
              you need to adjust your date, but haven’t been able to land on one yet. If this
              happens, it is best to let guests know as soon as possible that you are postponing,
              you’re still figuring things out, and will follow up with more information soon. Even
              if details are still in progress, we recommend giving your guests a heads up that
              last-minute changes are coming rather than waiting too long to tell someone. This way,
              guests can start to adjust potential travel plans and hotel reservations if they’re
              traveling to your wedding.
            </p>
            <p>
              You can share any update to your wedding plans with a beautiful change the date card
              or even add a postponement announcement to your wedding website. Zola makes it easy to
              do both.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do we keep our guests in the loop?',
        body: (
          <>
            <p>
              Should you choose to go with a TBD on your change the date, there are plenty of ways
              to keep your guests updated and in the know. For example, your change the date
              announcement could say something like “We’re changing the date and we’ll be in touch
              with more information soon. Visit our{' '}
              <a href="/wedding-planning/website" target="_blank" rel="noopener noreferrer">
                wedding website
              </a>{' '}
              for all the updated info!”. A wedding website from Zola is a great free tool that Zola
              offers to help you through a date change with ease.
            </p>
            <p>
              As for wedding postponement etiquette, it&apos;s best to include the engaged
              couple&apos;s name, the announcement itself, and updated wedding information if you
              have it. We recommend tailoring your postponement announcement based on the tone that
              you&apos;d like to set for your special day. Wording suggestions range from “save our
              new date,” to “we regretfully announce that our wedding has been postponed.” However
              you deliver the news, your guests will appreciate the update so that they can plan for
              the date change accordingly.
            </p>
          </>
        ),
      },
    ],
  },
  THANK_YOU: {
    subheading: 'Special designs created for special messages.',
    summary: (
      <p>
        Zola is here to make your custom thank you cards perfect inside and out! Our easily
        customizable thank you cards allow you to choose a design or upload your own. You can even
        personalize your card with a beautiful photo. Upload your favorite shot of you two from your
        wedding day or another photo you love. We’ll help make sure no guest’s generosity or time
        goes unnoticed.
      </p>
    ),
    faqContent: [
      {
        subheading:
          'The outside looks great, but what do I write!? How to Write a Wedding Thank You',
        body: (
          <p>
            When the time comes to put pen to paper, there are a few basic rules to follow when
            writing your wedding thank you cards. Not sure what to say? Customize any of our
            suggested thank you notes (or write your own), and we’ll print your unique message for
            each guest on the inside of a thank you card in a beautiful font. Here are some wording
            tips to help you get started.
          </p>
        ),
      },
      {
        subheading: 'Be Specific',
        body: (
          <>
            <p>
              Every good wedding thank you card says something specific about the gift you received.
              Mention exactly what you received as a gift and how you plan to use it. One exception
              is for monetary gifts: while it&apos;s great to mention how you plan to use the funds,
              it is best to not mention the exact amount.
            </p>
            <p>
              Remember, wedding thank you cards are meant for you to express your gratitude to your
              friends and family. You want your guest to know that you appreciate the time, effort,
              and thought that it took to select that specific present. Even if you’re not in love
              with the gift, make sure you say something nice about it (and never reveal that you
              plan on returning something). Whether guests contributed to your honeymoon fund or
              they got a generous gift for your home, it&apos;s always proper etiquette to send a
              wedding thank you.
            </p>
          </>
        ),
      },
      {
        subheading: 'Mention How You Plan to Use the Gift',
        body: (
          <>
            <p>
              The type of gift you receive will determine what you say about it. For example, if you
              receive a set of pots and pans, talk about some things you’re excited to cook. If you
              received a new duvet, talk about how good it looks on your bed. Keep this line short,
              sweet, and positive.
            </p>
            <p>
              Pro Tip: If you received money, you can say: “Thank you so much for your generous
              gift. We plan on putting it toward_______.”
            </p>
          </>
        ),
      },
      {
        subheading: 'Make It Personal',
        body: (
          <>
            <p>
              For example, if the guest was able to attend your wedding, say something about the
              time you spent together celebrating. This doesn’t have to be anything too significant,
              but, chances are, you or your significant other will have a memorable anecdote or
              special detail in mind about the person you’re sending a wedding thank you to.
            </p>
            <p>
              For some guests, it will be easy, like: “We were touched that you performed a reading
              at our ceremony.” For others, it may be something simple like: “We enjoyed dancing to
              ‘Jungle Boogie’ with you!” Don’t be afraid to include fun or playful details! Even if
              a moment seems trivial, the inclusion of a shared moment from your wedding shows you
              care.
            </p>
            <p>
              For gift-givers who couldn’t make it to your wedding, you can say something thoughtful
              like: “We missed you at the wedding, but know you were there in spirit. Can’t wait to
              see you ________!”
            </p>
          </>
        ),
      },
      {
        subheading: 'Use a Sentimental Sign-Off',
        body: (
          <>
            <p>
              For the most part, the guests you invited to your wedding are people you love and care
              for. As a result, you should use some kind of loving closing line to show adoration
              and appreciation for the recipient of the thank you card. It can be as flowery or as
              simple as you want, but many couples go with something along the lines of “All our
              love and gratitude.”
            </p>
            <p>
              If you’re sending wedding thank you cards to more casual friends, colleagues, or
              acquaintances, go with something like “Warmest regards” or “Best.”
            </p>
          </>
        ),
      },
      {
        subheading: 'Sign Your Wedding Thank You Cards as a Couple',
        body: (
          <>
            <p>
              Whether you are Mr. and Mr., Dr. and Mrs., or any combination thereof, you are finally
              a married couple! Your custom thank you cards are a great opportunity to sign your
              name as newlyweds in the same way you were introduced for the first time on your
              wedding day!
            </p>
            <p>
              Here is an example wedding thank you note you can use as a template to get the ball
              rolling:
            </p>
            <i>
              <p>Dear Aunt Linda,</p>
              <p>
                Thank you so much for the Le Creuset French Oven from our Zola registry! We will
                remember your hilarious toast every time we use it. We were so happy that you were
                able to join us for our wedding and take part in the celebration. As soon as we get
                back from our honeymoon, we are going to use the French Oven to make Chicken Pot
                Pie!
              </p>
              <p>
                All our love and gratitude,
                <br />
                Blanca Ortiz &amp; Christopher Marker
              </p>
            </i>
          </>
        ),
      },
    ],
  },
  ENCLOSURE: undefined, // On the PLP, the type will always be DETAILS
  DETAILS: {
    subheading: (
      <>
        Because you can’t fit it <em>all</em> on the invitation.
      </>
    ),
    summary: (
      <p>
        Enclosure cards are a common part of a{' '}
        <a href="/wedding-planning/invitations/shop" target="_blank" rel="noopener noreferrer">
          wedding invitation
        </a>{' '}
        suite. While the formal invitation typically includes important info about the ceremony
        (like the time, date, and location), enclosure cards can help relay additional details
        guests should know, like attire, transportation, reception details, and more.
      </p>
    ),
    faqContent: [
      {
        subheading: 'Why do we need a wedding enclosure card?',
        body: (
          <>
            <p>
              Couples often find that they’re unable to fit everything they want on their
              invitation. Including enclosure cards allows you to split up important information
              guests should know about your big day. That way, your formal invitation can be
              dedicated to the ceremony itself while your wedding enclosure cards take care of
              communicating the rest.
            </p>
            <p>
              Remember, adding too much information to your invitation can make it seem cluttered
              and confusing. We recommend having your wedding invitation stand alone as an elegant
              card focused on your ceremony. Then, your enclosure cards can help provide more
              detailed, personalized information on your other wedding events.
            </p>
          </>
        ),
      },
      {
        subheading: 'What info should we include on our wedding enclosure card?',
        body: (
          <>
            <p>
              Use your enclosure cards to communicate all the important wedding details that you
              couldn’t fit on your formal wedding invitation.
            </p>
            <p>
              Depending on the formality of your celebration, your total number of wedding events,
              and various other factors, you may want to include several different types of
              enclosure cards in your invitation suite to cover all your bases.
            </p>
          </>
        ),
      },
      {
        subheading: 'Directions Enclosure Card',
        body: (
          <p>
            Wedding enclosure cards are especially important if your ceremony and reception take
            place at different locations. Even if the reception is at a different venue on the same
            property, spelling it out is always a good idea. That way, you won’t have to worry
            whether guests know where to go on the big day.
          </p>
        ),
      },
      {
        subheading: 'Attire Enclosure Card',
        body: (
          <p>
            If you have a dress code for your wedding, make sure guests know what it is, so they can
            prepare accordingly. An enclosure card is a great way to tell your guests about what
            you’d like them to wear, whether it’s cocktail attire, dressy casual, or something more
            formal.
          </p>
        ),
      },
      {
        subheading: 'Wedding Website Card',
        body: (
          <>
            <p>
              Some couples prefer to share their wedding website URL with their guests on a separate
              enclosure card instead of on their save the date or invitation. Then, guests can visit
              their site to familiarize themselves with their full wedding itinerary.
            </p>
            <p>
              At Zola, you can match your entire invitation suite to a free, stunning{' '}
              <a href="/wedding-planning/website" target="_blank" rel="noopener noreferrer">
                wedding website
              </a>{' '}
              that takes just five minutes to set up. We make it so easy to personalize with photos,
              fun stories, and all your wedding details, from your schedule to travel info, FAQs,
              and more. Guests can even shop your{' '}
              <a href="/wedding-registry" target="_blank" rel="noopener noreferrer">
                Zola wedding registry
              </a>{' '}
              and RSVP right on your website!
            </p>
          </>
        ),
      },
      {
        subheading: 'Complete Invitation Suites',
        body: (
          <>
            <p>
              It’s important to remember that your wedding invitation and your wedding enclosure
              cards go hand-in-hand. These pieces of paper will live side-by-side in your wedding
              envelope as part of your entire invitation suite.
            </p>
            <p>
              Zola can help you match your wedding invitation, enclosure card, and wedding envelope
              to create the perfect thematic match! You can also find gorgeous matching save the
              dates, invitations, day-of paper, and more to complete your suite—all in one place.
            </p>
            <p>
              Our easily customizable templates, variety of affordable paper options, and wide range
              of thematic aesthetics are all at your disposal. Bring your wedding paper vision to
              life with Zola’s easy-to-use builder. Enjoy free free guest addressing, free
              envelopes, and, if your date changes, free change the dates with all save the date and
              invitation purchases. We’ve got you.
            </p>
          </>
        ),
      },
    ],
  },
  PLACE: {
    subheading: 'Wherever they sit, it’ll look good with these place cards.',
    summary: '',
    faqContent: [],
  },
  PROGRAM: {
    subheading: 'Wedding programs made to match your vibe.',
    summary: (
      <p>
        Just like a wedding video, your ceremony program is a snapshot of your incredible day. Your
        program will be a keepsake that you and your spouse can look back on years in the future and
        reminisce about the moment you joined your lives. Zola is dedicated to making your ceremony
        perfect with our fully customizable wedding program templates.
      </p>
    ),
    faqContent: [
      {
        subheading: 'What are some examples of great content to include in a wedding program?',
        body: (
          <p>
            Some couples choose to explain parts of their ceremony in their wedding program. They
            may elaborate on the meaning behind a chuppah, for example, or jumping the broom. They
            will also often name who is in the wedding party and list the agenda for the ceremony.
          </p>
        ),
      },
      {
        subheading: 'Why are wedding programs important to our ceremony?',
        body: (
          <p>
            Your wedding program provides important context for guests and helps them understand
            what’s happening. If you are incorporating any special traditions in your ceremony, the
            program can be a great place to explain the meaning behind the tradition for guests who
            may not be familiar. They can also add decorative flair to your ceremony! Choose a
            design that feels true to you as a couple and fits the vibe of your wedding.
          </p>
        ),
      },
      {
        subheading: 'How many programs are needed for the ceremony?',
        body: (
          <p>
            Order at least as many programs as you have guests attending, plus some extra. You don’t
            want to be caught empty handed if a guest loses their program and needs another! You may
            also want to keep one as a keepsake or to include in photos.
          </p>
        ),
      },
      {
        subheading: 'Why do we need a wedding program?',
        body: (
          <p>
            Your ceremony program should include a basic breakdown of the ceremony for your wedding
            day. With tons of wedding program templates and printing options, Zola makes it easy to
            customize your wedding program. Our ceremony program templates provide a basic outline
            that you can easily personalize with your unique details, leaving you with the ideal
            wedding ceremony program!
          </p>
        ),
      },
      {
        subheading: 'How do we make our program as unique as our wedding?',
        body: (
          <p>
            When choosing your wedding stationery, go with something that reflects your unique style
            as well as the theme of your special day. You can also add fun facts, your wedding
            hashtag, and other playful details to your wedding programs to showcase your
            personalities. There are tons of cool ways to spice up your ceremony programs—here are a
            few ideas inspired by real Zola couples!
          </p>
        ),
      },
      {
        subheading: 'Fun Facts and Stats About You Two',
        body: (
          <>
            <p>
              Numbers are a great way to get a closer glimpse into the lives of a couple. There’s a
              good chance that some of your wedding guests either know you or your spouse well, but
              not both of you together. Here’s your chance to open the door to who you are as a
              couple. Some examples are:
            </p>
            <p>
              <strong>Vacations Taken</strong>: 7
              <br />
              <strong>Years Dated</strong>: 2.5
              <br />
              <strong>Children</strong>: 0... for now
              <br />
              <strong>Combined Naps</strong>: 322
              <br />
              <strong>Dogs</strong>: 2 (not enough!)
              <br />
              <strong>Back Rubs Promised</strong>: 478
              <br />
              <strong>Back Rubs Given</strong>: 3
            </p>
            <p>
              You and your spouse will have a great time coming up with a collection of fun and
              unique stats. It will make your programs pop and it’s something fun to do as a couple
              to take some stress out of the planning process. Add these stats to the back of your
              ceremony program and we guarantee your guests will appreciate this playful info while
              waiting for the ceremony to begin.
            </p>
          </>
        ),
      },
      {
        subheading: 'Fun Facts About Your Wedding Party',
        body: (
          <>
            <p>
              Your wedding parties are an important part of your big day. Adding some fun facts
              about your bridesmaids and groomsmen to the back of your wedding programs is a great
              way for your guests to become acquainted with your closest friends and family.
            </p>
            <p>
              To save time, you can ask your wedding parties to fill out a fun, quick survey and
              choose everyone’s best answers for your program.
            </p>
          </>
        ),
      },
    ],
  },
  MENU: {
    subheading: (
      <>
        Menu designs as delicious as what’s <em>on</em> the menu.
      </>
    ),
    summary: (
      <>
        <p>
          Whether you are a gourmet foodie or a casual diner, everyone loves food. That’s why your
          wedding menu is such an important part of your big day.
        </p>
        <p>
          Sure, picking what food to serve is important, but we’re talking about the actual menu
          cards themselves. This gorgeous piece of paper will delight your guests as they sit down
          to dinner. The only thing better than finding out you’re getting crab cakes is when that
          info is printed on a beautiful Zola wedding reception food menu!
        </p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Why Your Menu Card Is Important',
        body: (
          <>
            <p>
              What’s one of the most memorable things about a wedding? The food, of course! Yes,
              your ceremony is going to be amazing but when it comes to your reception, your guests
              will always remember the food.
            </p>
            <p>
              You may not know this, but many guests like to save their wedding reception food menu
              as a souvenir. Your menu should be a reflection of the special atmosphere that your
              elegant dinner created for your guests.
            </p>
            <p>
              There is no better way to perk up someone’s appetite than with a gorgeous menu. Zola
              can help you customize your wedding menu with elegant fonts and gorgeous paper that
              perfectly matches your entire invitation suite!
            </p>
          </>
        ),
      },
      {
        subheading: 'Tips for Creating Your Menu Cards',
        body: (
          <>
            <strong>Make It Easy to Read</strong>
            <p>
              Font and paper are absolutely crucial when it comes to your wedding menu. There will
              be guests of all ages at your wedding and you need to make sure that the menu is
              legible and easy to read.
            </p>
            <p>
              Yes, a specialized calligrapher can make the letter “S” look like a work of art, but
              wouldn’t you rather your guest be able to read it? Zola’s fonts are elegant and unique
              while also being easy to read.
            </p>
            <strong>Keep It Simple</strong>
            <p>
              Wedding menu cards tell your guests about the different dishes that will be served, or
              offered, during your reception dinner service. No need to explain every dish or name
              every ingredient—just the basics will do. Your caterer will likely have the names and
              explanations of your catering menu ready to go, so feel free to ask them what you
              should write on your wedding menu. If you’re taking care of catering yourself, just
              make sure to keep your menu card short and sweet but still informative.
            </p>
            <strong>Customize It Up</strong>
            <p>
              At Zola, creating custom wedding menu cards is so fun and easy. Just like with your
              save the date cards and wedding invitations, you can choose from tons of unique paper
              types, fonts, and designs to make your menu cards truly your own.
            </p>
            <strong>Match Your Invitation Suite</strong>
            <p>
              When it comes to your wedding paper, continuity is key. You want to make sure that
              your wedding menu cards match your other stationery. While you can make your menu
              cards a different design from your invitations and save the dates, most couples like
              to stick to a general theme that matches their invitation suite. At the end of the
              day, it’s totally up to you.
            </p>
            <strong>Make Your Menu Pop</strong>
            <p>
              At Zola, you&apos;ll be able to look at a variety of different wedding menu ideas
              before your wedding day. If you are having a wedding caterer, talk with them
              beforehand to get all the information you need for your wedding reception menu. Zola
              offers paper options that will grab the attention of your guests as soon as they grab
              their menu. You can also add stunning custom foil to the text on your wedding menu
              cards to make your details really stand out. The extra shine will give your guests a
              luxurious dining experience they’ll never forget.
            </p>
          </>
        ),
      },
    ],
  },
  HOLIDAY: {
    subheading: 'Holiday cards that stand out from the crowd.',
    summary: (
      <>
        <p>
          Take your holiday cheer to the next level with our incredible collection of personalized
          holiday cards. Send a seasonal greeting to your loved ones, and share the joy you have
          found in each other with a customized holiday card design.
        </p>
        <p>
          Zola has a variety of different themes and templates so you can customize the perfect card
          that&apos;s unique to the two of you. Sending holiday cards as newlyweds is a sweet
          gesture that expresses your gratitude and love for your friends and family.
        </p>
        <p>It’s also a good idea to add a brief, thoughtful note to your holiday card template.</p>
      </>
    ),
    faqContent: [
      {
        subheading: 'Celebrate Your First Holiday Season as Newlyweds',
        body: (
          <p>
            The first year of marriage is all about celebrating milestones. In a year full of firsts
            as a married couple there is almost nothing better than the first holiday season! There
            is an added layer of holiday joy when you’re spending it with your S.O., and Zola can
            help you send your season’s greetings with personalized holiday cards and Christmas
            cards!
          </p>
        ),
      },
      {
        subheading: 'Customize Your Card With a Wedding Photo',
        body: (
          <p>
            Wedding photos are often a popular choice for holiday cards, which is why our holiday
            greeting cards are fully customizable. Use your favorite photo from your wedding or
            choose your favorite post-wedding shot! However you choose to decorate your holiday
            card, Zola’s templates and customizable design features make it easy for you to create
            the perfect holiday card design that’s both fun and unique.
          </p>
        ),
      },
      {
        subheading: 'What should we write on our holiday cards?',
        body: (
          <p>
            Zola has plenty of designs and holiday card templates to help you get the ball rolling.
            Whether you want to keep it short and sweet or personalize your message, Zola is here to
            help. And, our holiday cards always come with ample space to handwrite a personal
            message should you choose to!
          </p>
        ),
      },
      {
        subheading: 'Who should we send holiday cards to?',
        body: (
          <>
            <p>
              Anyone you want! You might still be in wedding mode when the holidays roll around so
              Zola recommends starting with your wedding guest list. You should already have a list
              of addresses from when you sent your wedding invitations. (If you ordered save the
              dates or invitations at Zola, we saved all your guests’ addresses for you and can
              print them on your envelopes for free.) From there, add people you maybe didn’t have
              room to invite, or out of town guests who couldn’t make it.
            </p>
            <p>
              You may feel hesitant about sending a holiday card to someone you didn’t invite to
              your wedding, especially if the picture on the card is you and your spouse on your
              wedding day. But your loved ones will appreciate the thoughtful gesture. So share some
              newlywed joy and send out those holiday cards!
            </p>
            <p>
              Zola’s holiday and Christmas cards have a theme for every winter holiday and a price
              for every budget. Our holiday cards are the perfect way to share your holiday cheer
              and get into the spirit of the season without stretching your wallet.
            </p>
          </>
        ),
      },
      {
        subheading: 'Paper Perks You Won’t Find Anywhere Else',
        body: (
          <>
            <p>
              At Zola, we always offer fast, free free guest addressing,free envelopes, and, if your
              date changes, free change the dates with all save the date and invitation purchases.
            </p>
            <p>
              Ready to get started with our custom holiday and Christmas cards? Get into the spirit
              of Christmas and holiday celebrations with our fun and customizable holiday cards
              today.
            </p>
          </>
        ),
      },
    ],
  },
  WEDDING_SHOWER_INVITATION: {
    subheading: 'Shower invitations so pretty, your wedding invites are jealous.',
    summary: '',
    faqContent: [],
  },
  TABLE_NUMBER: {
    subheading: 'Set the table with beautiful table numbers.',
    summary: (
      <>
        These are the signs in the center of each table that shows your guests where to sit. You can
        print front and back for maximum visibility. But they’re not just practical — think of them
        as the finishing touch for your{' '}
        <a href="/shop/category/reception" target="_blank">
          wedding tables
        </a>
        , truly customizable so you can seat your guests in style.
      </>
    ),
    faqContent: [
      {
        subheading: 'Why are wedding table numbers important?',
        body: (
          <>
            Picture the scene. It’s your wedding day. It’s been beautiful and emotional. And now
            you’re both ready to relax and enjoy the meal you’ve put so much thought into planning.
            Except none of your guests are sitting down. Instead of happy chatter and laughter you
            hear, “Where’s table 5?” and “Does anyone know where we’re meant to sit?”. That’s why
            you need wedding table numbers — a simple way for guests to know where to sit, all made
            to match your wedding invites and day-of paper (aka your{' '}
            <a href="/wedding-planning/programs/shop" target="_blank">
              programs
            </a>
            ,{' '}
            <a href="/wedding-planning/menus/shop" target="_blank">
              menus
            </a>
            , and{' '}
            <a href="/wedding-planning/place-cards/shop" target="_blank">
              place cards
            </a>
            ).
          </>
        ),
      },
      {
        subheading: 'How do wedding table numbers work?',
        body: (
          <>
            Once you’ve got your{' '}
            <a href="/expert-advice/how-to-create-a-wedding-seating-chart" target="_blank">
              seating chart sorted
            </a>
            , it’s time to move on to your table numbers. First choose your design. Zola has
            everything from elegant to rustic wedding table numbers, modern table numbers to
            vintage. Each is designed to elevate each table and match your vibe and decor (and, of
            course, clearly show that all-important table number). Once you’ve chosen the style,
            start customizing your number cards for the personal touch, then think about how you
            want to display them.{' '}
          </>
        ),
      },
      {
        subheading: 'Can I personalize my wedding table numbers?',
        body: (
          <>
            Yes! Your table numbers are made to match the rest of your wedding paper (that’s the
            invites your guests were so obsessed with, along with your programs, menus, place cards,
            and everything else you need for{' '}
            <a href="/wedding-planning/day-of" target="_blank">
              your wedding day
            </a>
            ). Change the colors, fonts, shape, and style or make them extra special with foil. And
            that’s before we get to the paper types. Smooth or eggshell? Recycled or triple thick?
            You can also add additional text as well to really personalize them — add your names,
            wedding date, or wedding hashtag.{' '}
          </>
        ),
      },
      {
        subheading: 'What are some creative ways to display table numbers?',
        body: (
          <>
            Looking for table number ideas for weddings? Let’s go. Once you’ve customized your
            wedding table numbers it’s time to think about how to show them off in the best way
            possible. You’ll want them to be easy to read, so guests know where to go (which is why
            Zola lets you print them on the back). But we’ve also got great ideas about how to
            display them. Add them to your flowers, centerpieces, or table decor. We also have a
            range of table number holders, whether you’re thinking{' '}
            <a
              href="/shop/product/essellesf_cedar_hexagon_place_card_holders_set6_white"
              target="_blank"
            >
              sleek and modern
            </a>
            , a{' '}
            <a href="/shop/product/petrahome_marble_card_holder" target="_blank">
              classic marble style
            </a>
            , or a{' '}
            <a
              href="/shop/product/esselle_sphere_with_gold_loop_signage_holder_natural"
              target="_blank"
            >
              natural, rustic look
            </a>
            .
          </>
        ),
      },
    ],
  },
  NAPKIN: {
    // TODO: hmyers - Add napkin subheading, summary and FAQ content
    subheading: '',
    summary: '',
    faqContent: [],
  },
  SIGNAGE: {
    // TODO: hmyers - Add signage subheading, summary and FAQ content
    subheading: ``,
    summary: '',
    faqContent: [],
  },
  STICKER: {
    // TODO: hmyers - Add sticker subheading, summary and FAQ content
    subheading: ``,
    summary: '',
    faqContent: [],
  },
  BELLY_BAND: undefined, // On the PLP, the type will always be PAPER_ADD_ONS
  VELLUM_JACKET: undefined, // On the PLP, the type will always be PAPER_ADD_ONS
  WAX_SEAL: undefined, // On the PLP, the type will always be PAPER_ADD_ONS
  PAPER_ADD_ONS: {
    subheading: 'Add the final flair to your invites.',
    summary: (
      <p>
        These are the finishing touches that will really impress your guests. Discover our wax
        seals, vellum jackets, and belly bands.
      </p>
    ),
    faqContent: [
      {
        subheading: 'What is a vellum jacket for wedding invitations?',
        body: (
          <p>
            It&apos;s a fancy name for a pretty fancy addition to your invitations and wedding
            paper. It folds over your paper to add even more elegance and drama to your wedding.
          </p>
        ),
      },
      {
        subheading: 'What are the benefits of using vellum jackets for wedding invitations?',
        body: (
          <p>
            Aside from the moment your guests gasp in delight at the anticipation of unwrapping
            them, it keeps individual elements safe and secure. Perfect if you&apos;ve got enclosure
            cards and RSVP cards along with your invites.
          </p>
        ),
      },
      {
        subheading: 'How do I assemble an invitation with a vellum jacket?',
        body: (
          <p>
            Simple! Put the invitation inside the jacket, address the envelopes as you would for any
            other wedding invitations. Your cards and the vellum jacket will go inside the envelope.
          </p>
        ),
      },
      {
        subheading: 'Can I order vellum jackets separately from the invitations?',
        body: <p>Yes! Order paper add-ons with your invitations or whenever you&apos;re ready.</p>,
      },
      {
        subheading: 'Can you mail invitations with wax seal?',
        body: (
          <p>
            Definitely! They look elegant but our wax seals have a modern sealing wax formula is
            durable and has a sturdy, professional grade adhesive backing to allow for mailing. And
            they&apos;re light enough (just 0.02oz per seal) to mail without any additional charges.
          </p>
        ),
      },
      {
        subheading: 'Does the post office accept letters with wax seals?',
        body: (
          <p>
            No problem. These wax seals are surprisingly light (just 0.02oz each) so won&apos;t
            cause any problems at the post office.
          </p>
        ),
      },
      {
        subheading: 'Do I need belly band for invitations?',
        body: (
          <p>
            You don&apos;t need them but you&apos;ll definitely want them. It&apos;s an easy way to
            elevate your invites. They&apos;re designed to fit all your favorite Zola invites in —
            the classic size in portrait, and petite in both landscape and portrait.
          </p>
        ),
      },
      {
        subheading: 'What are belly bands used for invitations?',
        body: (
          <>
            <p>
              Practically speaking they add help hold all your additional cards together. So if
              you&apos;re adding an RSVP and enclosure card to your invitations, a belly band keeps
              them secure. Belly bands measure 12.31 x 2 inches when flat, so you wrap them around
              your cards and use the included sticker to hold everything together.
            </p>
            <p>
              But there&apos;s so much more to it than that — it&apos;s an easy way to add even more
              flair and drama to the moment guests open your invitations.
            </p>
          </>
        ),
      },
      {
        subheading: 'How do you put a belly band on an invitation?',
        body: (
          <>
            <p>
              Don&apos;t worry, this isn&apos;t a major DIY task. Here&apos;s what you need to know:
            </p>
            <ul>
              <li>
                Stack your invitation suite in the following order, with your invitation on the
                bottom and all cards facing up:
                <ol>
                  <li>Invitation</li>
                  <li>Reception card</li>
                  <li>Directions card / Accommodations card</li>
                  <li>Reply card with reply card envelope</li>
                </ol>
              </li>
              <li>
                Center the belly band over your stacked suite and place the included clear sticker
                on one end.
              </li>
              <li>
                On a flat surface, flip your suite and band over (so everything is face down). Fold
                the non-stickered end around the suite first.
              </li>
              <li>
                Wrap the stickered end of the band tightly around the suite and adhere to the other
                end.
              </li>
              <li>
                Slide your completed invitation suite into the envelope with the cards facing up
              </li>
            </ul>
          </>
        ),
      },
    ],
  },
};
